<script lang="ts">
  import { page } from "$app/state";
  import { fade } from "svelte/transition";
  import { SearchHistory, SVG } from "$lib/components";
  import { authStore as Current } from "$lib/stores";
  import { onMount, onDestroy } from "svelte";
  import { goto } from "$app/navigation";
  import { cn } from "$lib/utility";
  import SubscriptionDialog from "$lib/components/dialogs/subscriptionDialog.svelte";
  import { SubscriptionAPI } from "$lib/api/subscription";
  import { routes } from "$lib/routes";
  import NavigationItems from "./navigationItems.svelte";
  import UserProfile from "./userProfile.svelte";

  import logoNormal from "$lib/images/logo/logo_light.svg";
  import logoDark from "$lib/images/logo/logo_dark.svg";
  import logoSmallNormal from "$lib/images/logo/icon_black_dot.svg";
  import logoSmallDark from "$lib/images/logo/icon_white_dot.svg";
  import { signInIcon } from "$lib/images/icons";
  import { browser } from "$app/environment";

  import { Sidebar } from "$lib/components/ui";
  let sidebar = Sidebar.useSidebar();

  import { useSidebar } from "$lib/components/ui/sidebar-custom/context.svelte";

  import { hamburgerMenuIcon } from "$lib/images/icons";

  import { mode } from "mode-watcher";

  const LOGOS = {
    normal: logoNormal,
    dark: logoDark,
    smallNormal: logoSmallNormal,
    smallDark: logoSmallDark,
  };

  // Derived states
  const isSidebarCollapsed = $derived(!sidebar.open && !sidebar.isMobile);
  const isSearchPage = $derived(page.url.pathname.startsWith("/search"));
  const isBlogPage = $derived(page.url.pathname.startsWith("/blog"));
  // const isListPage = $derived(page.url.pathname.startsWith("/lists"));
  // const isRootPage = $derived(page.url.pathname === "/");

  // Props with defaults
  let {
    className = "",
    variant = "sidebar",
    collapsible = "icon",
    isOpen = false,
    theme = "light",
    showAdminContent = false,
    showOnboardingContent = false,
    onClose = (event: MouseEvent) => {},
  } = $props<{
    className?: string;
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "icon" | "none" | "offcanvas";
    theme?: "light" | "dark";
    isOpen?: boolean;
    showAdminContent?: boolean;
    showOnboardingContent?: boolean;
    onClose?: (event: MouseEvent) => void;
  }>();

  // State management
  let dynamicLogo = $derived($mode === "light" ? LOGOS.normal : LOGOS.dark);
  let dynamicSmallLogo = $derived($mode === "light" ? LOGOS.smallNormal : LOGOS.smallDark);
  let currentPath = $state(page.url.pathname);

  let isSearchHistoryScrolled = $state(false);
  let sidebarContainer = $state<HTMLElement | null>(null);
  let showUpgradeDialog = $state(false);
  let isMobileDevice = $state(false);

  // Computed classes
  let baseClasses =
    "bg-theme-background-primary text-black dark:text-[var(--dark-text-primary)] border-[var(--light-background-secondary)] flex flex-col z-20 flex-shrink-0 flex-grow-0";
  let wrapperClasses = $derived(
    cn(
      baseClasses,
      "w-full h-screen",
      className,
      "bg-theme-background-primary text-black dark:text-[var(--dark-text-primary)] border-0",
      "overflow-hidden",
      sidebar.isMobile ? "border-0" : "border-r border-black dark:border-[var(--dark-background-secondary)]",
    ),
  );
  let borderColor = $derived($mode === "dark" ? "border-[var(--dark-background-secondary)]" : "border-black");

  // Effect to update current path
  $effect(() => {
    currentPath = page.url.pathname;
  });

  function handleUpgradeClick() {
    showUpgradeDialog = true;
  }

  function preventScroll(event: Event) {
    event.stopPropagation();
  }

  // Handle clicks outside the sidebar for mobile
  function handleClickOutside(event: MouseEvent) {
    if (isOpen && sidebarContainer && !sidebarContainer.contains(event.target as Node)) {
      event.preventDefault();
      onClose(event);
    }
  }

  function handleSidebarNavigation() {
    logger.log("handleSidebarNavigation: navigating to", currentPath);
    if (sidebar.isMobile) {
      logger.log("handleSidebarNavigation: closing sidebar");
      sidebar.toggle();
    }
  }

  function handleToggleSidebar() {
    sidebar.toggle();
  }

  onMount(async () => {
    isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if ($Current.isLoggedIn && !$Current.user?.plan) {
      await Current.updateUserPlan();
    }

    document.addEventListener("toggleSidebar", handleToggleSidebar);

    if (sidebarContainer) {
      sidebarContainer.addEventListener("scroll", preventScroll, { passive: false });
      sidebarContainer.addEventListener("touchmove", preventScroll, { passive: false });
    }
  });

  onDestroy(() => {
    if (browser) {
      document.removeEventListener("toggleSidebar", handleToggleSidebar);
    }

    if (sidebarContainer) {
      sidebarContainer.removeEventListener("scroll", preventScroll);
      sidebarContainer.removeEventListener("touchmove", preventScroll);
    }
  });

  $effect(() => {
    if (sidebar.collapsibleValue !== collapsible) {
      sidebar.collapsibleValue = collapsible;
    }
  });
</script>

<SubscriptionDialog bind:open={showUpgradeDialog} />

<Sidebar.Root {collapsible} {variant}>
  <Sidebar.Content class="sidebar-content lg:w-[50px] !z-50">
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <aside
      class={wrapperClasses}
      bind:this={sidebarContainer}
      onclick={(e) => {
        e.stopPropagation();
      }}
    >
      <!-- Header -->
      <Sidebar.Header class="p-0">
        <div
          class={cn(
            "transition-default flex h-18 flex-shrink-0 items-center justify-between",
            borderColor,
            isSidebarCollapsed ? "justify-center px-3" : "px-6",
          )}
        >
          <div class={cn("flex h-18 items-center justify-center gap-4.5")}>
            <button
              type="button"
              class={cn(
                "relative inline-flex items-center justify-center p-0 focus:outline-none",
                theme === "dark" ? "text-white" : "text-dark26",
              )}
              class:hidden={(!sidebar.isMobile && $Current.user?.id && !isBlogPage) ||
                (isSearchPage && !sidebar.isMobile) ||
                isSidebarCollapsed ||
                ($Current.user?.id && sidebar.open && sidebar.collapsible === "icon")}
              onclick={() => {
                sidebar.toggle();
              }}
            >
              <span class="sr-only">Close menu</span>
              <SVG file={hamburgerMenuIcon} className="min-w-9 min-h-9 max-w-9 w-full max-h-9" />
            </button>

            <button
              onclick={(e) => {
                goto($Current.isLoggedIn ? routes.tables : routes.root);
              }}
              class="w-full"
            >
              <img
                src={dynamicSmallLogo}
                alt="Logo"
                class={cn("mx-auto h-6.5 min-h-6.5 min-w-6.5", isSidebarCollapsed ? "inline-block" : "hidden")}
              />
              <img
                src={dynamicLogo}
                alt="Logo"
                class={cn("-mt-0.5 inline-block h-5", isSidebarCollapsed ? "hidden" : "inline-block")}
              />
            </button>
          </div>
        </div>
      </Sidebar.Header>

      {#if sidebar.isMobile}
        <!-- {#if !isSidebarCollapsed && ($Current.isLoggedIn || isSearchPage)}
          <div class="flex min-h-0 flex-grow flex-col">
            <div class="custom-scrollbar min-h-0 overflow-y-auto px-6">
              {#if $Current.isLoggedIn}
                <SearchHistory on:searchHistoryNavigation={handleSidebarNavigation} />
              {/if}
            </div>
          </div>
        {:else}
        {/if} -->

        {#if $Current.isLoggedIn}
          <!-- For logged in users -->
          <NavigationItems
            isCollapsed={isSidebarCollapsed}
            onlyItems={["chatsheet", "templates", "notebook"]}
            {borderColor}
            {handleSidebarNavigation}
            {handleUpgradeClick}
          />
        {:else}
          <!-- For logged out users -->
          <NavigationItems
            isCollapsed={isSidebarCollapsed}
            onlyItems={["features", "usecases", "integrations-public", "pricing", "company"]}
            {borderColor}
            {handleSidebarNavigation}
            {handleUpgradeClick}
          />
        {/if}

        <div
          class="border-text-theme-primary diagonal-lines flex-grow border-t-1 bg-theme-background-tertiary opacity-80 dark:border-theme-background-secondary"
        ></div>
        <NavigationItems
          isCollapsed={isSidebarCollapsed}
          onlyItems={["credits", "onboarding", "upgrade"]}
          {borderColor}
          {handleSidebarNavigation}
          {handleUpgradeClick}
        />

        {#if $Current.isLoggedIn}
          <UserProfile isCollapsed={isSidebarCollapsed} {borderColor} {sidebar} {handleSidebarNavigation} />
        {:else if isMobileDevice}
          <div
            class={cn(
              "sidebar-menuitem transition-default flex h-16 w-full items-center justify-start gap-1",
              isSidebarCollapsed ? "px-3" : "px-5",
              `border-t ${borderColor} bg-theme-background-secondary text-theme-text-primary`,
            )}
          >
            <div
              class={cn(
                "sidebar-menuitem transition-default flex w-full items-center justify-between gap-3 text-sm font-light hover:font-semibold ",
                isSidebarCollapsed ? "px-0" : "px-0.5",
              )}
            >
              <a href={routes.signIn} class={cn("flex w-full gap-3.5 p-0 px-1 uppercase hover:bg-transparent")}>
                <SVG className={cn("sidebar-icon transition-default w-5 h-5 min-h-5 min-w-5")} file={signInIcon} />
                <span class="text-nowrap" class:hidden={isSidebarCollapsed}>Log In</span>
              </a>
            </div>
          </div>
          <div
            class={cn(
              "sidebar-menuitem transition-default flex h-16 w-full items-center justify-start gap-1",
              isSidebarCollapsed ? "px-3" : "px-5",
              `border-t ${borderColor} bg-black text-white dark:bg-theme-green dark:text-black`,
            )}
          >
            <div
              class={cn(
                "sidebar-menuitem transition-default flex w-full items-center justify-between gap-3 text-sm font-light hover:font-semibold ",
                isSidebarCollapsed ? "px-0" : "px-0.5",
              )}
            >
              <a href={routes.bookDemo} class={cn("flex w-full gap-3.5 p-0 px-1 uppercase hover:bg-transparent")}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5 text-white dark:text-black">
                  <path
                    d="M21.5,2h-3.5V0h-1V2H7V0h-1V2H2.5C1.122,2,0,3.122,0,4.5V24H24V4.5c0-1.378-1.122-2.5-2.5-2.5ZM2.5,3H21.5c.827,0,1.5,.673,1.5,1.5v3.5H1v-3.5c0-.827,.673-1.5,1.5-1.5ZM1,23V9H23v14H1Z"
                    fill="currentColor"
                  />
                </svg>
                <span class="text-nowrap" class:hidden={isSidebarCollapsed}>Book a Demo</span>
              </a>
            </div>
          </div>
        {:else}
          <div
            class={cn(
              "sidebar-menuitem transition-default flex h-16 w-full items-center justify-start gap-1",
              isSidebarCollapsed ? "px-3" : "px-5",
              `border-t ${borderColor} bg-theme-background-secondary text-theme-text-primary`,
            )}
          >
            <div
              class={cn(
                "sidebar-menuitem transition-default flex w-full items-center justify-between gap-3 text-sm font-light hover:font-semibold ",
                isSidebarCollapsed ? "px-0" : "px-0.5",
              )}
            >
              <a href={routes.signIn} class={cn("flex w-full gap-3.5 p-0 px-1 uppercase hover:bg-transparent")}>
                <SVG className={cn("sidebar-icon transition-default w-5 h-5 min-h-5 min-w-5")} file={signInIcon} />
                <span class="text-nowrap" class:hidden={isSidebarCollapsed}>Log In</span>
              </a>
            </div>
          </div>
          <div
            class={cn(
              "sidebar-menuitem transition-default flex h-16 w-full items-center justify-start gap-1",
              isSidebarCollapsed ? "px-3" : "px-5",
              `border-t ${borderColor} bg-black text-white dark:bg-theme-green dark:text-black`,
            )}
          >
            <div
              class={cn(
                "sidebar-menuitem transition-default flex w-full items-center justify-between gap-3 text-sm font-light hover:font-semibold ",
                isSidebarCollapsed ? "px-0" : "px-0.5",
              )}
            >
              <a
                href={routes.signUp}
                class={cn("flex w-full gap-3.5 p-0 px-1 uppercase text-white hover:bg-transparent dark:text-black")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"
                  ><path
                    d="M9,12c3.309,0,6-2.691,6-6S12.309,0,9,0,3,2.691,3,6s2.691,6,6,6Zm0-11c2.757,0,5,2.243,5,5s-2.243,5-5,5-5-2.243-5-5S6.243,1,9,1Zm3.767,14.94c-1.151-.615-2.454-.94-3.767-.94-4.411,0-8,3.589-8,8v.5c0,.276-.224,.5-.5,.5s-.5-.224-.5-.5v-.5c0-4.962,4.038-9,9-9,1.478,0,2.943,.366,4.239,1.059,.244,.13,.335,.433,.205,.677-.13,.244-.433,.334-.677,.205Zm10.5-4.208c-.943-.944-2.592-.944-3.535,0l-7.707,7.707c-.661,.661-1.025,1.54-1.025,2.475v1.586c0,.276,.224,.5,.5,.5h1.586c.935,0,1.814-.364,2.475-1.025l7.707-7.707c.472-.472,.732-1.1,.732-1.768s-.26-1.296-.732-1.768Zm-.707,2.828l-7.707,7.707c-.472,.472-1.1,.732-1.768,.732h-1.086v-1.086c0-.668,.26-1.295,.732-1.768l7.707-7.707c.566-.566,1.555-.566,2.121,0,.283,.283,.439,.66,.439,1.061s-.156,.777-.439,1.061Z"
                  /></svg
                >
                <span class="text-nowrap" class:hidden={isSidebarCollapsed}>Sign Up For Free</span>
              </a>
            </div>
          </div>
        {/if}
      {:else}
        {#if $Current.isLoggedIn}
          <NavigationItems
            isCollapsed={isSidebarCollapsed}
            {borderColor}
            {handleSidebarNavigation}
            {handleUpgradeClick}
            {showAdminContent}
            {showOnboardingContent}
            onlyItems={["chatsheet", "templates"]}
          />

          <div class="flex flex-grow items-center justify-start border-t border-[var(--dark-background-secondary)]">
            <span
              class="w-[60px] rotate-[-90deg] text-nowrap text-2xs font-thin uppercase tracking-widest text-black dark:text-[var(--dark-text-primary)]"
              >ChatSheet</span
            >
          </div>

          <NavigationItems
            isCollapsed={isSidebarCollapsed}
            {borderColor}
            {handleSidebarNavigation}
            {handleUpgradeClick}
            {showAdminContent}
            {showOnboardingContent}
            onlyItems={[
              "notebook",
              "learn",
              "admin",
              "notifications",
              "integrations",
              "upgrade",
              "credits",
              "onboarding",
            ]}
          />
        {:else}
          <!-- Logged out  -->
          <div class="flex flex-grow items-center justify-start border-t border-[var(--dark-background-secondary)]">
            <span
              class="-mt-2 rotate-[-90deg] text-nowrap text-2xs font-thin uppercase tracking-widest text-black dark:text-[var(--dark-text-primary)]"
              >ChatSheet</span
            >
          </div>
          <NavigationItems
            isCollapsed={isSidebarCollapsed}
            {borderColor}
            {handleSidebarNavigation}
            {handleUpgradeClick}
            {showAdminContent}
            {showOnboardingContent}
            onlyItems={["search", "pricing"]}
          />
        {/if}

        <UserProfile isCollapsed={isSidebarCollapsed} {borderColor} {sidebar} {handleSidebarNavigation} />
      {/if}
    </aside>
  </Sidebar.Content>
</Sidebar.Root>

<style>
  :global(body.sidebar-open) {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: fixed;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background-color: transparent;
  }
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
  .sidebar-menuitem {
    font-weight: 300;
  }
  .sidebar-menuitem:hover {
    font-weight: 500;
  }

  .diagonal-lines {
    background-image: repeating-linear-gradient(-45deg, transparent, transparent 40px, black 1px, black 41px);
  }

  :global(html) {
    height: -webkit-fill-available;
  }

  :global(body) {
    min-height: -webkit-fill-available;
  }

  @supports (-webkit-touch-callout: none) {
    .sidebar-content {
      height: -webkit-fill-available !important;
    }

    aside {
      height: -webkit-fill-available !important;
      max-height: -webkit-fill-available !important;
    }
  }

  @media screen and (max-width: 768px) {
    aside {
      height: 100% !important;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    aside > * {
      flex-shrink: 0;
    }

    aside > .diagonal-lines {
      flex-grow: 1;
      flex-shrink: 1;
      overflow: hidden;
    }

    aside > :last-child {
      margin-bottom: env(safe-area-inset-bottom, 0px);
      padding-bottom: env(safe-area-inset-bottom, 0px);
      position: relative;
      z-index: 10;
    }
  }
</style>
